import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Button, message, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import { useTranslation } from "react-i18next";
import HomeButton from "../components/HomeButton";

const { Title } = Typography;

const OCR: React.FC = () => {
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleOCR = async (values: { imageFile: File | null }) => {
    if (!values.imageFile) {
      message.error("Please upload a PNG image");
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(values.imageFile);
      const token = localStorage.getItem("token");

      reader.onloadend = async () => {
        const base64String = reader.result?.toString().split(",")[1];
        setPreviewImage(reader.result?.toString() || null);
        const response = await axios.post(
          `${API_BASE_URL}/zhadiger/api/image/ocr`,
          {
            text: base64String,
          },
          {
            headers: {
              Authorization: `${token}`,

              "ngrok-skip-browser-warning": "true",
            },
          }
        );

        setExtractedText(response.data);
        message.success("Image has been processed for text extraction!");
      };

      reader.onerror = (error) => {
        message.error("Error converting image to base64 format");
        console.error("Error in FileReader:", error);
        setLoading(false);
      };
    } catch (error) {
      message.error("Error processing image for OCR");
      console.error("Error in OCR API call:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 ml-4 pt-4 ">
        <HomeButton />
      </div>
      <div className="max-w-[70%] mx-auto mt-10 p-6 bg-white border border-gray-200 rounded-lg shadow-md">
        <Title level={2} className="text-center font-bold text-gray-700">
          {t("ocr")}
        </Title>

        <div className="flex flex-col md:flex-row md:space-x-4 mt-6">
          <div className="w-full md:w-1/2 p-4 bg-white border border-gray-200 rounded-lg shadow-md">
            <Formik
              initialValues={{ imageFile: null }}
              onSubmit={(values) => handleOCR(values)}
            >
              {({ setFieldValue }) => (
                <Form className="space-y-4">
                  <Field name="imageFile">
                    {() => (
                      <Upload
                        beforeUpload={(file) => {
                          setFieldValue("imageFile", file);
                          return false;
                        }}
                        maxCount={1}
                        accept=".png, jpg, jpeg"
                      >
                        <Button icon={<UploadOutlined />} block>
                          {t("upload_image")}
                        </Button>
                      </Upload>
                    )}
                  </Field>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 transition duration-200"
                  >
                    {loading ? t("processing") : t("extract_text")}
                  </Button>
                </Form>
              )}
            </Formik>

            {previewImage && (
              <div className="mt-6 text-center">
                <Title level={4} className="text-gray-600">
                  {t("image_preview")}
                </Title>
                <img
                  src={previewImage}
                  alt="Uploaded Preview"
                  className="w-full h-auto border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>

          <div className="w-full md:w-1/2 p-4 bg-white border border-gray-200 rounded-lg shadow-md h-auto">
            <Title level={4} className="text-gray-600 text-center">
              {t("extracted_text")}
            </Title>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={15}
              readOnly
              value={extractedText || t("extracted_text_title") || ""}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OCR;
